/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): hex-to-rgba conversion homed in const/ so pure `utils/` leaves can
 * value-import it without a forbidden cross-utils sibling dependency (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`).
 *
 * That ban is exactly what left this conversion spelled TWICE: a `utils/` leaf owned the
 * exported copy while `utils/styles/interaction-tokens.ts` carried a byte-different hand-inlined twin under the same
 * name, with a docstring recording the inline as deliberate because "the utils floor admits only const/types/zod". One
 * name answering to two bodies is what `QUAL-PRIVATE-FN-SHADOWS-SHARED-EXPORT-001` reports; const/ is the floor both
 * sides may reach, so the constraint that forced the copy is removed rather than documented.
 */

import { clamp } from '@federa/const/numeric-clamp';
import { HEX_COLOR_PATTERN } from '@federa/const/hex-color-pattern';

/** Parse a hex color into its RGB components, or null when invalid. */
function parseHexComponents(hex: string): [number, number, number] | null {
	const result = HEX_COLOR_PATTERN.exec(hex);
	if (!result) return null;
	return [Number.parseInt(result[1] as string, 16), Number.parseInt(result[2] as string, 16), Number.parseInt(result[3] as string, 16)];
}

/**
 * Convert a hex color to an RGBA string with the given opacity (0-1, clamped): hexToRgba('#FF0000', 0.5) yields
 * 'rgba(255, 0, 0, 0.5)'. THROWS on anything this parser cannot read.
 *
 * THE THROW IS THE CURE, and it replaces a documented degrade-to-transparent-black (2026-08-26). The old contract said
 * an unparseable input degraded rather than threw, "which is the behaviour both retired copies had and every consumer
 * relies on for a missing token" -- and that claim was false in the one direction that mattered: the SDL publishes
 * every colour as a `var(--color-*)` reference, so the FIVE rendering-tier call sites that read `css-refs.colors` were
 * handing this parser a string it could never read, and all five rendered `rgba(0, 0, 0, alpha)`. The witness is
 * `getIconStyles` in `components/feedback/ConfirmDialog.styles.ts`: the glyph carried its variant colour while the disc
 * behind it was the same opaque black for all four variants, so the failure was invisible in review. A silent degrade
 * makes a wrong colour indistinguishable from a right one at every future call site; a throw makes it a dev-time crash
 * on the first render. No caller now passes a non-hex -- all five were converted to `tokenAlpha` in the same change --
 * so nothing degrading is load-bearing.
 *
 * For a design-token REFERENCE use `tokenAlpha` (`const/token-alpha.ts`), which emits a paint-time `color-mix()`
 * instead of parsing channels. `DS-TOKEN-REF-INTO-HEX-PARSER-001` reports the static spelling of the confusion before
 * this throw can be reached.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the file header gives the pinning reason -- both former homes are
 * `utils/` leaves that may not value-import each other, so `const/` is the one floor the whole cohort can reach.
 */
export function hexToRgba(hex: string, opacityValue: number): string {
	const rgb = parseHexComponents(hex);
	if (!rgb) {
		throw new Error(
			`hexToRgba expects a #RRGGBB hex literal and received ${JSON.stringify(hex)}. A design-token reference resolves at paint time and has no channels to read -- use tokenAlpha() from @federa/const/token-alpha for one.`,
		);
	}
	const clamped = clamp(opacityValue, 0, 1);
	return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${clamped})`;
}

/** The overlay's light stop: a quarter-strength tint. */
const OVERLAY_TINT_ALPHA = 0.25;

/** The overlay's dark stop: an 80% scrim, dense enough for text over the image beneath. */
const OVERLAY_SCRIM_ALPHA = 0.8;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): a top-to-bottom image overlay from a light TINT of one hex to a dark
 * SCRIM of another, over {@link hexToRgba} -- the `gradients.tertiaryOverlay` token each brand's colour leaf publishes
 * (`brand/federa/tokens/colors.ts`, `brand/ink/tokens/colors.ts`) with its own two hexes.
 */
export function tintToScrimOverlay(tintHex: string, scrimHex: string): string {
	return `linear-gradient(180deg, ${hexToRgba(tintHex, OVERLAY_TINT_ALPHA)}, ${hexToRgba(scrimHex, OVERLAY_SCRIM_ALPHA)})`;
}
